import { type CommonColors, createTheme } from '@mui/material/styles';

const spaceGroteskFont = 'Space Grotesk';
const spaceMonoFont = 'Space Mono';

const neutralColors = {
	N050: '#F2F2F2',
	N150: '#D9D9D9',
	N250: '#BFBFBF',
	N350: '#A6A6A6',
	N450: '#8C8C8C',
	N550: '#737373',
	N650: '#595959',
	N750: '#404040',
	N850: '#262626',
	N950: '#0D0D0D',
};

const primaryAndSecondaryColors = {
	primaryWhite: '#FFFFFF',
	secondary500: '#FADD55',
	secondary800: '#514B32',
};

const gradientColors = {
	gBlue: '#83F4FF',
	gPink: '#D8B2F6',
	gYellow: '#FEF0AC',
}

const gradientStops = {
	colorfulStops: [0, 54.5, 100],
	holographicGradient1Stops: [0, 50.48, 77.48, 100],
}

export const colors = {
	white: '#FFFFFF',
	black: '#000000',

	tLightBlue: 'rgba(137, 200, 247, 0.14)',
	green: '#56BA46',
	lightGreen: '#2B4120',
	red: '#F25641',
	lightRed: '#BE4A3B',
	lightYellow: '#BBB632',
	primary: '#2F98F9',
	primaryGradient: 'linear-gradient(180deg, #2F98F9 0%, #2F5BF9 100%)',
	tWhiteLight: 'rgba(212, 224, 255, 0.08)',
	tWhiteBold: 'rgba(181, 202, 255, 0.15)',
	tBlack: 'rgba(0, 0, 0, 0.2)',
	mainBox: 'rgba(0, 0, 0, 0.45)',
	background: 'linear-gradient(295.52deg, #161616 4.74%, #0B061C 88.17%)',
	darkPrimary: '#1F3250',
	darkPrimaryHover: '#2B4875',
	alwaysWhite: '#FFFFFF',
	tableBg: '#152A4A',
	transparentBg: 'rgba(0, 0, 0, 0.2)',
	transparentBgDark: 'rgba(0, 0, 0, 0.4)',
	buttonBackground: '#1F3250',
	buttonBackgroundHover: '#0E213F',
	toastBgRed: '#4A2B2B',
	toastBgNatural: '#2E334D',
	toastBgGreen: '#365439',
	tBlueLight: 'rgba(137, 200, 247, 0.14)',
	linkHover: '#94B8FF',
	inputTokenBox: '#202431',
	cardBg: '#151923',

	//  ** Rebrand **

	// Primary & Secondary
	...primaryAndSecondaryColors,

	// Natural
	...neutralColors,

	...gradientColors,
	...gradientStops,

	// App
	borderPrimary: neutralColors.N750,
	borderSecondary: '#333333',
	textPrimary: primaryAndSecondaryColors.primaryWhite,
	textSecondary: neutralColors.N450,
	textTertiary: neutralColors.N750,
	appTextDisabled: neutralColors.N250,
	textBlack: neutralColors.N950,
	primaryWhite: primaryAndSecondaryColors.primaryWhite,
	// App_Background
	backgroundSecondary: '#25252B',
	backgroundPrimary: '#1A1A1F',
	backgroundTertiary: neutralColors.N750,
	backgroundTilesOverlay: '#bebebe',

	colorfulBorder:  `linear-gradient(90deg, ${gradientColors.gBlue} ${gradientStops.colorfulStops[0]}%, ${gradientColors.gPink} ${gradientStops.colorfulStops[1]}%, ${gradientColors.gYellow} ${gradientStops.colorfulStops[2]}%)`,
	colorfulBorderVertical:  `linear-gradient(180deg, ${gradientColors.gBlue} ${gradientStops.colorfulStops[0]}%, ${gradientColors.gPink} ${gradientStops.colorfulStops[1]}%, ${gradientColors.gYellow} ${gradientStops.colorfulStops[2]}%)`,

	// App/colors
	appColorsGreen: '#63D98B',
	appColorsGreenLight: '#27442F',
	appColorsYellow: '#FBD740',
	appColorsYellowLight: '#443D1F',
	appColorsRed: '#F27B5F',
	appColorsRedLight: '#492A26',
	// App_Spec(METHOD) color
	appMethodColorSwift: '#FFFF00',
	appMethodColorMctp: '#71E2DD',
	appMethodColorWormhole: '#AAABFF',

	// Background
	backgroundBridge: 'linear-gradient(0deg, rgba(12,3,17,1) 0%, rgba(16,13,30,1) 100%)',
	backgroundStars: 'linear-gradient(0deg, rgba(12,3,17,1) 0%, rgba(22,23,12,1) 100%);',
	backgroundExplorer: 'linear-gradient(0deg, rgba(12,3,17,1) 0%, rgba(19,25,34,1) 100%)',

	// Landing
	landingHigh: '#F2F5FB',
	landingMedium: '#667390',
	landingBorder: '#313954',
	landingPrimary: '#F4D35E',
} as const satisfies CommonColors;

export const typography = {
	numsHeadingBold: {
		fontWeight: 700,
		fontSize: 28,
		lineHeight: 'normal',
		fontFamily: `${spaceMonoFont} !important`,
	},
	numsHeading: {
		fontWeight: 400,
		fontSize: 28,
		lineHeight: 'normal',
		fontFamily: `${spaceMonoFont} !important`,
	},
	numsBigBold: {
		fontWeight: 700,
		fontSize: 24,
		lineHeight: 'normal',
		fontFamily: `${spaceMonoFont} !important`,
	},
	numsBig: {
		fontWeight: 400,
		fontSize: 24,
		lineHeight: 'normal',
		fontFamily: `${spaceMonoFont} !important`,
	},
	appHugeBold: {
		fontWeight: 700,
		fontSize: 20,
		lineHeight: 'normal',
	},
	appHugeRegular: {
		fontWeight: 400,
		fontSize: 20,
		lineHeight: 'normal',
	},
	appMediumBold: {
		fontWeight: 700,
		fontSize: 16,
		lineHeight: 'normal',
	},
	appMediumRegular: {
		fontWeight: 400,
		fontSize: 16,
		lineHeight: 'normal',
	},
	appSmallBold: {
		fontWeight: 700,
		fontSize: 14,
		lineHeight: 'normal',
	},
	appSmallRegular: {
		fontWeight: 400,
		fontSize: 14,
		lineHeight: 'normal',
	},
	appTinyBold: {
		fontWeight: 700,
		fontSize: 12,
		lineHeight: 'normal',
	},
	appTinyRegular: {
		fontWeight: 400,
		fontSize: 12,
		lineHeight: 'normal',
	},
	appCaptionBold: {
		fontWeight: 700,
		fontSize: 10,
		lineHeight: 'normal',
	},
	appCaptionRegular: {
		fontWeight: 400,
		fontSize: 10,
		lineHeight: 'normal',
	},
} as const;

declare module '@mui/material/styles' {
	interface TypographyVariants {
		numsHeadingBold: React.CSSProperties,
		numsHeading: React.CSSProperties,
		numsBigBold: React.CSSProperties,
		numsBig: React.CSSProperties,
		appHugeBold: React.CSSProperties,
		appHugeRegular: React.CSSProperties,
		appMediumBold: React.CSSProperties,
		appMediumRegular: React.CSSProperties,
		appSmallBold: React.CSSProperties,
		appSmallRegular: React.CSSProperties,
		appTinyBold: React.CSSProperties,
		appTinyRegular: React.CSSProperties,
		appCaptionBold: React.CSSProperties,
		appCaptionRegular: React.CSSProperties,
	}

	// allow configuration using `createTheme`
	interface TypographyVariantsOptions {
		numsHeadingBold?: React.CSSProperties,
		numsHeading?: React.CSSProperties,
		numsBigBold?: React.CSSProperties,
		numsBig?: React.CSSProperties,
		appHugeBold?: React.CSSProperties,
		appHugeRegular?: React.CSSProperties,
		appMediumBold?: React.CSSProperties,
		appMediumRegular?: React.CSSProperties,
		appSmallBold?: React.CSSProperties,
		appSmallRegular?: React.CSSProperties,
		appTinyBold?: React.CSSProperties,
		appTinyRegular?: React.CSSProperties,
		appCaptionBold?: React.CSSProperties,
		appCaptionRegular?: React.CSSProperties,
	}
}

declare module '@mui/material/styles' {
	interface BreakpointOverrides {
		tablet: true;
		mobile: true;
	}
}

declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		numsHeadingBold: true;
		numsHeading: true;
		numsBigBold: true;
		numsBig: true;
		appHugeBold: true;
		appHugeRegular: true;
		appMediumBold: true;
		appMediumRegular: true;
		appSmallBold: true;
		appSmallRegular: true;
		appTinyBold: true;
		appTinyRegular: true;
		appCaptionBold: true;
		appCaptionRegular: true;
	}
}
declare module "@mui/material/styles/createPalette" {
	interface CommonColors {
		tLightBlue: string;
		green: string;
		lightGreen: string;
		red: string;
		lightRed: string;
		lightYellow: string;
		primary: string;
		primaryGradient: string;
		tWhiteLight: string;
		tWhiteBold: string;
		tBlack: string;
		mainBox: string;
		background: string;
		darkPrimary: string;
		darkPrimaryHover: string;
		alwaysWhite: string;
		tableBg: string;
		transparentBg: string;
		transparentBgDark: string;
		buttonBackground: string;
		buttonBackgroundHover: string;
		toastBgRed: string;
		toastBgNatural: string;
		toastBgGreen: string;
		tBlueLight: string;
		linkHover: string;
		inputTokenBox: string;
		cardBg: string;

		//  ** Rebrand **

		// Primary & Secondary
		primaryWhite: string;
		secondary500: string;
		secondary800: string;

		// Natural
		N950: string;
		N850: string;
		N750: string;
		N650: string;
		N550: string;
		N450: string;
		N350: string;
		N250: string;
		N150: string;
		N050: string;

		gBlue: string;
		gPink: string;
		gYellow: string;

		colorfulStops: number[];
		holographicGradient1Stops: number[];

		// App
		borderPrimary: string;
		borderSecondary: string;
		textPrimary: string;
		textSecondary: string;
		textTertiary: string;
		appTextDisabled: string;
		textBlack: string;
		// App_Background
		backgroundSecondary: string;
		backgroundPrimary: string;
		backgroundTertiary: string;
		backgroundTilesOverlay: string;
		colorfulBorder: string;
		colorfulBorderVertical: string;

		appColorsGreen: string;
		appColorsGreenLight: string;
		appColorsYellow: string;
		appColorsYellowLight: string;
		appColorsRed: string;
		appColorsRedLight: string;

		// App_Spec color
		appMethodColorSwift: string;
		appMethodColorMctp: string;
		appMethodColorWormhole: string;

		// Background
		backgroundBridge: string;
		backgroundStars: string;
		backgroundExplorer: string;

		// Landing
		landingHigh: string;
		landingMedium: string;
		landingBorder: string;
		landingPrimary: string;
	}
}


declare module '@mui/material/InputBase' {
	interface InputBasePropsSizeOverrides {
		large: true;
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsVariantOverrides {
		withBg: true;
	}
}

const theme  = createTheme({
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1536,
			tablet: 760,
			mobile: 410,
		}
	},
	palette: {
		mode: 'dark',
		primary: {
			main: colors.primary,
			dark: colors.darkPrimary,
		},
		common: colors,
		text: {
			primary: colors.textSecondary,
		},
		error: {
			main: colors.red,
		},
		background: {
			// default: 'radial-gradient(60.6% 109.04% at 68.26% 25.83%, #232641 0%, #1D2232 100%)',
		}
	},
	typography,
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				'*': {
					margin: 0,
					padding: 0,
					boxSizing: 'border-box',
					fontFamily: spaceGroteskFont,
				},
				html: {
					background: colors.backgroundSecondary,
				},
				body: {
					WebkitTapHighlightColor: 'rgba(255, 255, 255, 0)',
					fontFamily: spaceGroteskFont,
					background: colors.backgroundSecondary,
					'& .walletconnect-modal__base': {
						color: '#000',
					},
				},
				button: {
					fontFamily: spaceGroteskFont,
				},
				input: {
					fontFamily: spaceGroteskFont,
					fontWeight: 400,
					fontSize: '14px',
					'::placeholder': {
						color: colors.textSecondary,
						fontFamily: spaceGroteskFont,
						fontWeight: 400,
						fontSize: '14px',
					},
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: colors.borderPrimary,
					marginTop: 12,
					marginBottom: 12,
				},
			}
		},
		MuiSkeleton: {
			styleOverrides: {
				rectangular: {
					borderRadius: 8,
				},
				text: {
					borderRadius: 8,
				}
			},
			defaultProps: {
				animation: 'wave',
				variant: 'rectangular',
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					padding: '16px 24px 24px',
					backgroundColor: colors.N850,
					backgroundImage: 'none',
					borderRadius: 24,
				},
				paperWidthSm: {
					width: 440,
				},
				paperWidthMd: {
					width: 500,
				},
			}
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					paddingTop: 0,
					paddingLeft: 0,
					paddingRight: 0,
					paddingBottom: 8,
				},
			},
		},
		MuiDialogContent: {
			styleOverrides: {
				root: {
					paddingLeft: 0,
					paddingTop: 0,
					paddingRight: 0,
					paddingBottom: 0,
					'&:last-child': {
						paddingBottom: 0
					}
				},
			},
		},
		MuiStepConnector: {
			styleOverrides: {
				root: {
					marginLeft: 16,
					// '& .MuiStepConnector-line': {
					// 	borderColor: colors.primary
					// }
				},
				line: {
					borderLeftWidth: 3,
					minHeight: 30,
					borderColor: colors.borderPrimary,
				},
				// completed: {
				// 	'&.MuiStepConnector-line': {
				// 		borderColor: colors.primary,
				// 	},
				// 	'& .MuiStepConnector-line': {
				// 		borderColor: colors.primary,
				// 	}
				// },
			},
		},
		MuiStepLabel: {
			styleOverrides: {
				iconContainer: {
					paddingRight: 48,
				},
				// label: {
				// 	...typography.appMediumRegular,
				// }
			},
		},
		MuiListItemText: {
			styleOverrides: {
				primary: {
					...typography.appMediumRegular,
				},
				secondary: {
					color: colors.N250,
					...typography.appCaptionRegular,
				}
			}
		},
		MuiListItemAvatar: {
			styleOverrides: {
				root: {
					minWidth: 52,
				}
			}
		},
		MuiDialogActions: {
			styleOverrides: {
				root: {
					paddingLeft: 0,
					paddingBottom: 0,
					paddingRight: 0,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					fontFamily: spaceGroteskFont,
					textTransform: 'none',
					transition: 'all 0.3s ease-out',
					'&.Mui-disabled' : {
						backgroundColor: colors.N650,
						color: '#FFF !important',
					},
				},
				sizeMedium: {
					fontFamily: spaceGroteskFont,
					fontWeight: 700,
					fontSize: 15,
					lineHeight: '25px',
					height: 39,
					padding: '10px 26px',
				},
				contained: {
					color: colors.N050,
					boxShadow: 'none',
					'&:hover': {
						backgroundColor: '#3385D0',
						boxShadow: 'none',
					},
					'&.Mui-disabled' : {
						backgroundColor: colors.N450,
						color: `${colors.N250} !important`,
					},
					'&.MuiButton-containedError': {
						'&:hover': {
							backgroundColor: '#CA3A26'
						},
					}
				},
				containedSizeMedium: {
					borderRadius: 20,
					minWidth: 145,
				},
				sizeSmall: {
					height: 31,
					borderRadius: 40,
					paddingLeft: 20,
					paddingRight: 20,
					...typography.appTinyRegular,
				},
				outlinedSizeMedium: {
					borderRadius: 20,
					minWidth: 145,
				},
				textSizeMedium: {
					padding: '2px 20px',
					...typography.appTinyRegular,
				},
				sizeLarge: {
					...typography.appMediumBold,
					textTransform: 'none',
					height: 51,
					padding: '18px 31px',
					minWidth: 175,
					paddingTop: 12,
					paddingBottom: 12,
				},
				containedSizeLarge: {
					borderRadius: 40,
				},
				outlinedSizeLarge: {
					borderRadius: 27,
				},
				startIcon: {
					marginRight: 10,
					marginLeft: 0,
				},
				endIcon: {
					marginRight: 0,
					marginLeft: 10,
				},
			},
			variants: [
				{
					props: { variant: 'withBg' },
					style: {
						color: colors.primary,
						backgroundColor: colors.transparentBgDark,
					},
				},
			],
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundColor: colors.N850,
					boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25);',
					backgroundImage: 'none',
				},
			}
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					padding: 8,
					borderRadius: 12,
					'&:hover': {
						backgroundColor: colors.N650,
					}
				}
			}
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					fontFamily: spaceGroteskFont,
				}
			}
		},
		MuiPagination: {
			styleOverrides: {
				ul: {
					border: '1px solid #263051',
					borderRadius: 8,
					'& li': {
						borderRight: '1px solid #263051',
						'&last-child': {
							borderRight: 'none',
						}
					}
				},
			}
		},
		MuiPaginationItem: {
			styleOverrides: {
				root: {
					border: 'none',
					height: 41,
					minWidth: 41,
					margin: 0,
					boxShadow: 'none',
					borderRadius: 0,
					fontSize: 15,
					'&.Mui-selected': {
						backgroundColor: colors.N650,
						color: colors.N050
					}
				},
				ellipsis: {
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}
			}
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					height: 31,
					borderRadius: 40,
					border: `1px solid ${colors.borderPrimary}`,
					'&.Mui-focused': {
						border: `1px solid ${colors.N250}`,
					}
				},
				input: {
					...typography.appSmallRegular,
					paddingTop: 0,
					fontFamily: spaceGroteskFont,
					paddingBottom: 0,
					outline: 'none',
					border: 'none',
					'&::placeholder': {
						...typography.appSmallRegular,
						color: colors.textPrimary,
					}
				},
				notchedOutline: {
					border: 'none',
				}
			},
			variants: [
				{
					props: { size: 'large' },
					style: {
						height: 59,
						borderRadius: 12,
						'& .MuiOutlinedInput-input': {
							paddingTop: 16,
							fontFamily: spaceGroteskFont,
							paddingBottom: 16,
							paddingLeft: 24,
							paddingRight: 24,
							...typography.appMediumRegular,
							'&::placeholder': {
								...typography.appMediumRegular,
								color: colors.N350,
							}
						}
					}
				}
			]
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					backgroundColor: colors.N050,
					borderRadius: 12,
					padding: 8,
					fontFamily: spaceGroteskFont,
					color: colors.N850,
					...typography,
				},
				arrow: {
					color: colors.N050,
					fontSize: 17,
				}
			},
			defaultProps: {
				placement: 'top',
			}
		},
		MuiTabs: {
			styleOverrides: {
				indicator: {
					height: 4,
					borderRadius: 2,
				}
			}
		},
		MuiMenu: {
			styleOverrides: {
				paper: {
					borderRadius: 64,
					backgroundColor: colors.N850,
				},
				root: {
					borderRadius: 64,
				},
				list: {
					padding: 0,
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					padding: 12,
					...typography.appSmallBold,
					fontFamily: spaceGroteskFont,
					color: colors.textPrimary,
					'&:hover': {
						backgroundColor: colors.backgroundSecondary,
					},
					'&.Mui-selected': {
						backgroundColor: colors.backgroundSecondary,
					},
					'&.Mui-selected:hover': {
						backgroundColor: colors.backgroundSecondary,
					}
				}
			}
		},
		MuiSelect: {
			styleOverrides: {
				select: {
					...typography.appSmallBold,
					fontFamily: spaceGroteskFont,
					color: colors.textPrimary,
				}
			}
		}
	},
});

export default theme;
